/**
 * "Saldo Vello" on the rider record (D737b, ADR-0150, doc 32): the rider's signed balance in the scope's market - credit minus open debt - with the two lines that make
 * it up, the debt explained ("cobrado na próxima corrida em dinheiro"), the latest movements and the adjustments waiting for a second operator.
 *
 * Shown to an operator with `rider_wallet:view`. With `rider_wallet:propose` the card opens a drawer to add or take credit away (a direction, an amount in the market's
 * currency, a reason the direction admits and a note). The answer says whether the adjustment was APPLIED at once (at or below the governed threshold and the operator's
 * own limit) or sent for approval (four eyes): a second operator with `rider_wallet:approve` decides it - on this card or on the approvals screen - and never the maker.
 * A credit can be taken away only up to what the rider has: a manual debit never creates a debt. Money is formatted by the console's format adapter; a refusal is worded
 * by the central vocabulary. No native dialog is ever opened.
 */
import { type ReactElement, useState } from 'react';
import { isIso4217Currency } from '@vello/core';
import { type DirectoryUser, type RiderWalletAdminView, type RiderWalletDirectionView } from '@vello/api-client';
import { Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, Field, Ink, MoneyInput, Notice, RecordRow, RecordSection, Select, Spacer, Text, TextArea } from '@vello/web-theme';
import {
  RIDER_WALLET_DIRECTIONS,
  RIDER_WALLET_DRAFT_MESSAGE,
  RIDER_WALLET_NOTE_MAX,
  isRiderWalletReason,
  pendingAdjustments,
  riderWalletAuthority,
  riderWalletBalanceModel,
  riderWalletDraftIssue,
  riderWalletMovements,
  riderWalletProposeBlock,
  riderWalletProposeOutcome,
  riderWalletReasonsFor,
  withoutClosingStop,
  type RiderWalletTone,
} from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { DrawerStack } from './drawer-stack';
import { PersonCard, useCardRead } from './person-card';
import { usePersonFormat } from './person-format';
import { RiderWalletPendingItem } from './rider-wallet-pending';

const MOVEMENTS_SHOWN = 10;
const toneInk = (tone: RiderWalletTone): 'success' | 'danger' | 'secondary' => (tone === 'credit' ? 'success' : tone === 'debit' ? 'danger' : 'secondary');

type Result = { readonly tone: 'success' | 'warning' | 'danger'; readonly text: string } | undefined;

export function PersonWalletCard({ user }: { user: DirectoryUser }): ReactElement {
  const { runtime, scope, grants, me } = useAdmin();
  const { t, lang } = useT();
  const { v, fail, refusal } = useVocab();
  const fmt = usePersonFormat();
  const market = scope.current.city;
  const { mayPropose, mayApprove } = riderWalletAuthority(grants);
  const { state, reload } = useCardRead(market !== undefined ? () => runtime.riderWallet.get(user.id, market) : undefined, [runtime, user.id, market]);
  const [result, setResult] = useState<Result>(undefined);
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState<RiderWalletDirectionView>('CREDIT');
  const [amount, setAmount] = useState<bigint | undefined>(undefined);
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | undefined>(undefined);

  const view: RiderWalletAdminView | undefined = state?.status === 'ready' ? state.value : undefined;
  const block = riderWalletProposeBlock(user.id, me?.id, mayPropose);
  const money = (minor: bigint, currency: string): string => fmt.money({ amountMinor: minor.toString(), currency });

  const openDrawer = (): void => {
    setDirection('CREDIT');
    setAmount(undefined);
    setReasonCode('');
    setNote('');
    setFormError(undefined);
    setOpen(true);
  };

  const issue = view !== undefined ? riderWalletDraftIssue({ direction, amountMinor: amount, reasonCode, note, creditMinor: view.creditMinor }) : 'AMOUNT_REQUIRED';

  const propose = async (): Promise<void> => {
    if (market === undefined || amount === undefined || issue !== undefined || !isRiderWalletReason(reasonCode)) return;
    setBusy(true);
    setFormError(undefined);
    const res = await runtime.riderWallet.propose(user.id, { marketId: market, direction, amountMinor: amount, reasonCode, note: note.trim() });
    setBusy(false);
    if (!res.ok) {
      setFormError(t('rwallet.error', { reason: withoutClosingStop(refusal('refusal.admin.rider_wallet', res.error) ?? fail(res.error)) }));
      // A refusal that says the rider's credit moved (409) means the card is stale.
      if (res.error.status === 409) reload();
      return;
    }
    setOpen(false);
    setResult(riderWalletProposeOutcome(res.value.adjustment) === 'APPLIED' ? { tone: 'success', text: t('rwallet.result.applied') } : { tone: 'warning', text: t('rwallet.result.pending') });
    reload();
  };

  const decided = (outcome: 'APPROVED' | 'REJECTED' | 'REFUSED'): void => {
    if (outcome === 'APPROVED') setResult({ tone: 'success', text: t('rwallet.result.approved') });
    else if (outcome === 'REJECTED') setResult({ tone: 'success', text: t('rwallet.result.rejected') });
    reload();
  };

  return (
    <>
      {/* Outside the card: the card goes back to its skeleton while it reads again, and the answer must stay. */}
      {result !== undefined && (
        <div data-testid="rwallet-result"><Notice tone={result.tone}>{result.text}</Notice></div>
      )}
      <PersonCard
        title={t('rwallet.title')}
        what={t('rwallet.what')}
        state={state}
        reload={reload}
        heldBack={t('rwallet.noMarket')}
        actions={
          mayPropose && view !== undefined && block !== 'OWN_ACCOUNT' ? (
            <Button variant="secondary" size="sm" onClick={openDrawer} data-testid="rwallet-adjust">
              {t('rwallet.adjust')}
            </Button>
          ) : undefined
        }
      >
        {(w) => {
          const balance = riderWalletBalanceModel(w);
          const movements = riderWalletMovements(w.entries);
          const pending = pendingAdjustments(w.pending);
          return (
            <>
              <RecordSection>
                <Text as="h3" role="label">
                  {t('rwallet.balance.label')}
                </Text>
                <Text role="headline" mono tone={toneInk(balance.tone)} data-testid="rwallet-balance">
                  {balance.tone === 'zero'
                    ? t('rwallet.balance.zero')
                    : t(balance.tone === 'credit' ? 'rwallet.balance.credit' : 'rwallet.balance.debit', { amount: money(balance.absoluteMinor, w.currency) })}
                </Text>
                <Text as="p" role="caption" tone="secondary" data-testid="rwallet-balance-note">
                  {t(balance.tone === 'credit' ? 'rwallet.balance.creditNote' : balance.tone === 'debit' ? 'rwallet.balance.debitNote' : 'rwallet.balance.zeroNote')}
                </Text>
                <RecordRow dense>
                  <span>{t('rwallet.line.credit')}</span>
                  <span className="vello-grow" />
                  <Ink mono data-testid="rwallet-credit">{money(w.creditMinor, w.currency)}</Ink>
                </RecordRow>
                <RecordRow dense>
                  <span>{t('rwallet.line.debt')}</span>
                  <span className="vello-grow" />
                  <Ink mono data-testid="rwallet-debt">{money(w.debtMinor, w.currency)}</Ink>
                </RecordRow>
              </RecordSection>

              {pending.length > 0 && (
                <RecordSection>
                  <Text as="h3" role="label">
                    {t('rwallet.pending')}
                  </Text>
                  {pending.map((a) => (
                    <RiderWalletPendingItem key={a.adjustmentId} adjustment={a} mayApprove={mayApprove} onDecided={decided} />
                  ))}
                </RecordSection>
              )}

              <RecordSection>
                <Text as="h3" role="label">
                  {t('rwallet.movements')}
                </Text>
                {movements.length === 0 ? (
                  <Text as="p" role="caption" tone="secondary">
                    {t('rwallet.movements.empty')}
                  </Text>
                ) : (
                  movements.slice(0, MOVEMENTS_SHOWN).map((m) => (
                    <RecordRow key={m.id} dense>
                      <Badge tone={m.tone === 'credit' ? 'success' : m.tone === 'debit' ? 'danger' : 'neutral'}>{v('rider_wallet.entry_kind', m.kind)}</Badge>
                      {m.adjustmentReason !== undefined && <span>{v('rider_wallet.reason', m.adjustmentReason)}</span>}
                      <span className="vello-grow" />
                      <Ink mono>
                        {m.tone === 'credit' ? '+' : ''}
                        {money(m.signedMinor, w.currency)}
                      </Ink>
                      <Ink mono>{fmt.when(m.at)}</Ink>
                    </RecordRow>
                  ))
                )}
                {movements.length > MOVEMENTS_SHOWN && (
                  <Text as="p" role="caption" tone="secondary">
                    {t('rwallet.movements.more', { count: String(MOVEMENTS_SHOWN) })}
                  </Text>
                )}
              </RecordSection>
              {mayPropose && block === 'OWN_ACCOUNT' && (
                <Text as="p" role="caption" tone="secondary" data-testid="rwallet-own-account">
                  {t('rwallet.drawer.ownAccount')}
                </Text>
              )}
            </>
          );
        }}
      </PersonCard>

      <Drawer open={open} onClose={() => setOpen(false)} labelledBy="rwallet-drawer-title">
        <DrawerHeader
          titleId="rwallet-drawer-title"
          title={t('rwallet.drawer.title')}
          subtitle={view !== undefined ? t('rwallet.drawer.subtitle', { balance: money(view.balanceMinor, view.currency) }) : undefined}
        />
        <DrawerBody>
          <DrawerStack>
            {formError !== undefined && (
              <div data-testid="rwallet-form-error"><Notice tone="danger">{formError}</Notice></div>
            )}
            <Field label={t('rwallet.field.direction')} htmlFor="rwallet-direction">
              <Select
                id="rwallet-direction"
                value={direction}
                disabled={busy}
                onChange={(ev) => {
                  const next: RiderWalletDirectionView = ev.target.value === 'DEBIT' ? 'DEBIT' : 'CREDIT';
                  setDirection(next);
                  if (!(riderWalletReasonsFor(next) as readonly string[]).includes(reasonCode)) setReasonCode('');
                }}
              >
                {RIDER_WALLET_DIRECTIONS.map((d) => (
                  <option key={d} value={d}>
                    {v('rider_wallet.direction', d)}
                  </option>
                ))}
              </Select>
            </Field>
            {view !== undefined && isIso4217Currency(view.currency) && (
              <MoneyInput
                id="rwallet-amount"
                label={t('rwallet.field.amount')}
                currency={view.currency}
                locale={lang}
                valueMinor={amount}
                onChangeMinor={setAmount}
                issueText={(i) => v('format.money_input_issue', i)}
                required
              />
            )}
            <Text as="p" role="caption" tone="secondary">
              {direction === 'DEBIT' && view !== undefined ? t('rwallet.drawer.debitHint', { credit: money(view.creditMinor, view.currency) }) : t('rwallet.drawer.creditHint')}
            </Text>
            <Field label={t('rwallet.field.reason')} htmlFor="rwallet-reason">
              <Select id="rwallet-reason" value={reasonCode} disabled={busy} onChange={(ev) => setReasonCode(ev.target.value)} required aria-required="true">
                <option value="">{t('rwallet.field.reason.choose')}</option>
                {riderWalletReasonsFor(direction).map((r) => (
                  <option key={r} value={r}>
                    {v('rider_wallet.reason', r)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('rwallet.field.note')} htmlFor="rwallet-note" hint={t('rwallet.field.note.hint')}>
              <TextArea id="rwallet-note" value={note} maxLength={RIDER_WALLET_NOTE_MAX} disabled={busy} onChange={(ev) => setNote(ev.target.value)} />
            </Field>
            {(issue === 'DEBIT_ABOVE_CREDIT' || issue === 'NOTE_TOO_LONG') && (
              <Text as="p" role="caption" tone="danger" data-testid="rwallet-draft-issue">
                {t(RIDER_WALLET_DRAFT_MESSAGE[issue])}
              </Text>
            )}
            <Text as="p" role="caption" tone="secondary">
              {t('rwallet.drawer.fourEyes')}
            </Text>
          </DrawerStack>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" disabled={busy} onClick={() => setOpen(false)}>
            {t('ui.cancel')}
          </Button>
          <Spacer />
          <Button variant="primary" disabled={busy || issue !== undefined} onClick={() => void propose()} data-testid="rwallet-send">
            {t('rwallet.drawer.send')}
          </Button>
        </DrawerFooter>
      </Drawer>
    </>
  );
}
